import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Warning, Rocket, Gift, Lock, ArrowRight, FileText, Receipt, Calculator, ShieldCheck, Bell, Trash, ArrowsClockwise } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Hard blocking modal shown whenever user.subscription.is_active === false.
 * Cannot be dismissed. Shows 2 paths: subscribe (Stripe) or redeem a promo code.
 * Also shows saved-data (loss aversion) + delete-account double-confirm flow.
 */
export default function TrialExpiredGate({ user, onLogout }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState('main'); // 'main' | 'promo' | 'delete1' | 'delete2'
  const [promo, setPromo] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    axios.get(`${API}/auth/me/saved-data`, { withCredentials: true })
      .then(r => setSaved(r.data))
      .catch(() => { /* silent */ });
  }, []);

  const redeem = async () => {
    if (!promo.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/promo-codes/redeem`, { code: promo.trim() }, { withCredentials: true });
      toast.success('Codice attivato! 🎉');
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Codice non valido');
    }
    setBusy(false);
  };

  const deleteAccount = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/auth/delete-account`, { withCredentials: true });
      toast.success('Account cancellato. A presto!');
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    } catch (e) {
      toast.error('Errore nella cancellazione. Riprova.');
      setBusy(false);
    }
  };

  // Build saved data rows
  const savedRows = saved ? [
    { icon: FileText, label: 'contratti', count: saved.contracts, color: 'text-zinc-900' },
    { icon: Receipt, label: 'ricevute', count: saved.receipts, color: 'text-teal-700' },
    { icon: Calculator, label: 'righe di contabilità', count: saved.bookings + saved.expenses, color: 'text-emerald-700' },
    { icon: ShieldCheck, label: 'schedine scaricate', count: saved.schedine, color: 'text-indigo-700' },
    { icon: Bell, label: 'alert eventi attivi', count: saved.event_alerts, color: 'text-rose-600' },
  ].filter(r => r.count > 0) : [];

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-y-auto"
      data-testid="trial-expired-gate"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-zinc-950/85 backdrop-blur-md" />

      <div className="relative w-full max-w-md bg-white shadow-2xl overflow-hidden animate-modalIn my-4">
        <style dangerouslySetInnerHTML={{__html:`
          @keyframes modalIn { from { opacity: 0; transform: translateY(20px) scale(.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
          .animate-modalIn { animation: modalIn .4s cubic-bezier(.2,.8,.2,1) both; }
          @keyframes shake { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-2px)} 40%,80%{transform:translateX(2px)} }
          .shake { animation: shake .6s ease-in-out .2s both; }
          @keyframes pulse-danger { 0%,100%{box-shadow:0 0 0 0 rgba(220,38,38,.5)} 50%{box-shadow:0 0 0 8px rgba(220,38,38,0)} }
          .pulse-danger { animation: pulse-danger 1.8s ease-in-out infinite; }
        `}} />

        {/* Hero (dynamic based on mode) */}
        {mode !== 'delete1' && mode !== 'delete2' && (
          <div className="relative bg-gradient-to-br from-red-600 via-rose-600 to-pink-700 text-white p-6 sm:p-7 overflow-hidden">
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 bg-white/20 border border-white/30 backdrop-blur flex items-center justify-center shake">
                  <Lock size={18} weight="fill" className="text-white" />
                </div>
                <span className="text-[10px] font-black uppercase tracking-[0.25em] bg-white/20 border border-white/30 backdrop-blur px-2 py-1">
                  Prova terminata
                </span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight leading-tight mb-1.5">
                I sette giorni sono volati! 🎢
              </h2>
              <p className="text-sm text-white/85 leading-relaxed">
                Ci siamo divertiti, vero? Per continuare serve solo un piccolo gesto — poi la piattaforma torna tutta tua.
              </p>
            </div>
          </div>
        )}

        {/* Dangerous zone header */}
        {(mode === 'delete1' || mode === 'delete2') && (
          <div className="relative bg-gradient-to-br from-red-700 via-red-800 to-zinc-900 text-white p-6 sm:p-7">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 bg-red-600/30 border border-red-400/40 backdrop-blur flex items-center justify-center">
                <Trash size={18} weight="fill" className="text-red-200" />
              </div>
              <span className="text-[10px] font-black uppercase tracking-[0.25em] bg-red-600/30 border border-red-400/40 backdrop-blur px-2 py-1">
                Zona rossa
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight leading-tight mb-1.5">
              {mode === 'delete1' ? 'Proprio addio? 💔' : 'Sei sicurissimo, eh? 🧨'}
            </h2>
            <p className="text-sm text-red-100 leading-relaxed">
              {mode === 'delete1'
                ? "Prima di premere, leggi cosa succede. Poi decidi tu in tranquillità."
                : "Ultima chance: da qui non si torna indietro. Nessun backup, nessun ripristino, nessuna seconda chance."}
            </p>
          </div>
        )}

        {/* Body */}
        <div className="p-6 sm:p-7">
          {mode === 'main' && (
            <>
              {/* Saved data (loss aversion) */}
              {savedRows.length > 0 && (
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 p-4 mb-5" data-testid="saved-data-panel">
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-700 mb-2.5">
                    ✨ Il tuo lavoro ti aspetta
                  </p>
                  <ul className="space-y-1.5 mb-2.5">
                    {savedRows.map((r, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-zinc-800">
                        <r.icon size={14} weight="bold" className={r.color} />
                        <span><strong className="font-black">{r.count}</strong> {r.label}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-emerald-800 leading-snug pt-2 border-t border-emerald-200/70">
                    Tornano tutti operativi nel <strong>millisecondo successivo</strong> al pagamento. Zero setup, zero re-inserimento.
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <button
                  onClick={() => navigate('/subscribe?plan=yearly')}
                  className="group w-full text-left relative border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-teal-50 p-4 hover:shadow-lg transition-all hover:-translate-y-0.5"
                  data-testid="gate-yearly-btn"
                >
                  <span className="absolute -top-2.5 right-3 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5">
                    Risparmi 40%
                  </span>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-0.5">Annuale</p>
                      <p className="text-xl font-black text-zinc-900">€49,99<span className="text-xs font-medium text-zinc-500"> /anno</span></p>
                      <p className="text-[11px] text-zinc-500 mt-0.5">~ €3,33 al mese · dimentica il rinnovo</p>
                    </div>
                    <ArrowRight size={18} weight="bold" className="text-emerald-600 group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>

                <button
                  onClick={() => navigate('/subscribe?plan=monthly')}
                  className="group w-full text-left relative border border-zinc-300 bg-white p-4 hover:border-zinc-900 transition-all hover:-translate-y-0.5"
                  data-testid="gate-monthly-btn"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-0.5">Mensile</p>
                      <p className="text-xl font-black text-zinc-900">€6,99<span className="text-xs font-medium text-zinc-500"> /mese</span></p>
                      <p className="text-[11px] text-zinc-500 mt-0.5">Meno di una margherita · disdici quando vuoi</p>
                    </div>
                    <ArrowRight size={18} weight="bold" className="text-zinc-400 group-hover:text-zinc-900 group-hover:translate-x-1 transition-all" />
                  </div>
                </button>
              </div>

              <div className="mt-5 pt-5 border-t border-zinc-100 text-center space-y-2">
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/self-unlock`, {
                        method: 'POST', credentials: 'include',
                      });
                      const d = await res.json();
                      if (d.unlocked) {
                        alert('Abbonamento ripristinato! Ricarico la pagina.');
                        window.location.reload();
                      } else {
                        alert(d.message || 'Nessun abbonamento attivo trovato su Stripe con questa email.');
                      }
                    } catch { alert('Errore durante lo sblocco. Riprova.'); }
                  }}
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 transition-colors"
                  data-testid="gate-self-unlock-btn"
                >
                  <ArrowsClockwise size={14} weight="bold" /> Ho già pagato, sblocca ora
                </button>
                <button
                  onClick={() => setMode('promo')}
                  className="block mx-auto inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-600 hover:text-zinc-900 transition-colors"
                  data-testid="gate-promo-link"
                >
                  <Gift size={14} weight="bold" /> Ho un codice promo
                </button>
              </div>

              <button
                onClick={onLogout}
                className="mt-3 w-full text-[11px] text-zinc-400 hover:text-zinc-700 underline"
                data-testid="gate-logout-btn"
              >
                Esci e torna più tardi
              </button>

              {/* Danger zone divider */}
              <div className="mt-6 pt-4 border-t border-dashed border-zinc-200 text-center">
                <button
                  onClick={() => setMode('delete1')}
                  className="text-[11px] text-zinc-400 hover:text-red-600 transition-colors"
                  data-testid="gate-delete-link"
                >
                  Se non ti è proprio piaciuto e non vuoi più saperne nulla di noi →
                </button>
              </div>
            </>
          )}

          {mode === 'promo' && (
            <div className="space-y-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Codice promo</p>
                <Input
                  value={promo}
                  onChange={(e) => setPromo(e.target.value.toUpperCase())}
                  placeholder="ES. ESTATE2026"
                  className="rounded-none h-11 font-mono text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && redeem()}
                  data-testid="gate-promo-input"
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setMode('main'); setPromo(''); }} className="rounded-none flex-1">Indietro</Button>
                <Button onClick={redeem} disabled={!promo.trim() || busy} className="rounded-none flex-1 bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider text-xs" data-testid="gate-redeem-btn">
                  {busy ? 'Verifica...' : <><Rocket size={14} weight="bold" className="mr-1.5" /> Attiva</>}
                </Button>
              </div>
            </div>
          )}

          {mode === 'delete1' && (
            <div className="space-y-4" data-testid="delete-step-1">
              <p className="text-sm text-zinc-700 leading-relaxed">
                Premi <strong>Cancella subito tutto</strong> e in pochi secondi cancelleremo:
              </p>
              <ul className="space-y-1.5 text-[13px] text-zinc-600 bg-zinc-50 border border-zinc-200 p-4">
                <li className="flex items-start gap-2"><Trash size={12} weight="bold" className="text-red-500 mt-1 flex-shrink-0" />Il tuo account e la password</li>
                <li className="flex items-start gap-2"><Trash size={12} weight="bold" className="text-red-500 mt-1 flex-shrink-0" />Tutti i contratti, ricevute, righe di contabilità</li>
                <li className="flex items-start gap-2"><Trash size={12} weight="bold" className="text-red-500 mt-1 flex-shrink-0" />Schedine Alloggiati e configurazioni</li>
                <li className="flex items-start gap-2"><Trash size={12} weight="bold" className="text-red-500 mt-1 flex-shrink-0" />Proprietà, template, alert sugli eventi</li>
                <li className="flex items-start gap-2"><Trash size={12} weight="bold" className="text-red-500 mt-1 flex-shrink-0" />Ogni traccia del tuo passaggio</li>
              </ul>
              <p className="text-[11px] text-zinc-500 leading-relaxed">
                Ti arriverà un'<strong>email di conferma</strong> a cancellazione avvenuta. Se poi ci penserai su, <em>no problem</em> — potrai registrarti di nuovo in qualsiasi momento (ovviamente da zero).
              </p>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setMode('main')} className="rounded-none flex-1">
                  Ci ho ripensato
                </Button>
                <Button
                  onClick={() => setMode('delete2')}
                  className="rounded-none flex-1 bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider text-xs"
                  data-testid="gate-delete-step1-btn"
                >
                  <Trash size={14} weight="bold" className="mr-1.5" /> Cancella subito tutto
                </Button>
              </div>
            </div>
          )}

          {mode === 'delete2' && (
            <div className="space-y-4" data-testid="delete-step-2">
              <div className="bg-red-50 border-2 border-red-500 p-5 text-center">
                <p className="text-4xl mb-2">🧨</p>
                <p className="font-black text-lg text-red-900 mb-2 leading-tight">
                  Ultima chiamata
                </p>
                <p className="text-sm text-red-800 leading-relaxed">
                  Questo è <strong>veramente</strong> l'ultimo click. Dopo, il tuo account sparisce per sempre. Niente backup, niente "scusa ho cambiato idea".
                </p>
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  variant="outline"
                  onClick={() => setMode('main')}
                  className="rounded-none flex-1 border-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50 font-bold"
                  data-testid="gate-delete-abort-btn"
                >
                  Resto con voi ❤️
                </Button>
                <Button
                  onClick={deleteAccount}
                  disabled={busy}
                  className="rounded-none flex-1 bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider text-xs pulse-danger"
                  data-testid="gate-delete-confirm-btn"
                >
                  {busy ? 'Cancello...' : <><Trash size={14} weight="bold" className="mr-1.5" /> Cancella tutto</>}
                </Button>
              </div>
            </div>
          )}

          {mode !== 'delete1' && mode !== 'delete2' && (
            <div className="mt-5 pt-4 border-t border-zinc-100 text-center">
              <p className="text-[10px] text-zinc-400 leading-relaxed">
                ⚠ I tuoi dati sono salvi. Tornano tutti come li avevi lasciati<br />
                nel millisecondo successivo al pagamento.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
